import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, PLATFORM_LABELS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Radar, X, Plus, Sparkles, CheckCircle2, Search, Instagram, Facebook, Globe, Gem, Twitter, Linkedin, Youtube, MessageCircle, Music2 } from "lucide-react";

const PLATFORM_META = {
  instagram: { label: "Instagram", icon: Instagram },
  facebook: { label: "Facebook", icon: Facebook },
  x: { label: "X (Twitter)", icon: Twitter },
  linkedin: { label: "LinkedIn", icon: Linkedin },
  tiktok: { label: "TikTok", icon: Music2 },
  youtube: { label: "YouTube", icon: Youtube },
  reddit: { label: "Reddit", icon: MessageCircle },
  google: { label: "Web / Presse", icon: Globe },
};
const PLATFORMS = ["instagram", "facebook", "x", "linkedin", "tiktok", "youtube", "reddit", "google"];

export default function Scanner() {
  const navigate = useNavigate();
  const [terms, setTerms] = useState([]);
  const [input, setInput] = useState("");
  const [platforms, setPlatforms] = useState(["instagram", "facebook", "x", "linkedin", "tiktok", "youtube", "reddit", "google"]);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/keywords").then((r) => {
      if (r.data.length) setTerms(r.data.map((k) => k.term));
    });
  }, []);

  const addTerm = () => {
    const t = input.trim();
    if (t && !terms.includes(t)) {
      setTerms([...terms, t]);
      setInput("");
    }
  };

  const togglePlatform = (p) => {
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const startScan = async () => {
    if (terms.length === 0) {
      toast.error("Bitte mindestens einen geschützten Namen hinzufügen.");
      return;
    }
    if (platforms.length === 0) {
      toast.error("Bitte mindestens eine Plattform auswählen.");
      return;
    }
    setScanning(true);
    setResult(null);
    setProgress(8);
    const timer = setInterval(() => {
      setProgress((p) => (p < 88 ? p + Math.random() * 12 : p));
    }, 500);

    try {
      const r = await api.post("/scan", { keywords: terms, platforms });
      setProgress(100);
      setResult(r.data);
      toast.success(`Scan abgeschlossen: ${r.data.created} neue Fälle erfasst.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan fehlgeschlagen.");
    } finally {
      clearInterval(timer);
      setScanning(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="scanner-page">
      <div className="relative overflow-hidden rounded-xl bg-[#0A0A0A] p-8 text-white shadow-sm md:p-10">
        <Gem className="absolute -right-4 -top-6 h-44 w-44 text-white/[0.04]" strokeWidth={0.8} />
        <div className="absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-[#A6A4A9]/10 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-md border border-white/20">
            <Radar className="h-6 w-6" strokeWidth={1.4} />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold md:text-4xl">Scanner</h1>
            <p className="mt-1 text-sm text-white/70">
              Durchsucht öffentlich indexierte Profile und bewertet Treffer automatisch per KI.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8 lg:col-span-3">
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Geschützte Namen / Keywords</h3>
          <p className="mt-1 text-sm text-[#6B6A6E]">
            Begriffe, nach denen gesucht wird (z. B. Markenname, Produktname).
          </p>

          <div className="mt-4 flex gap-2">
            <Input
              data-testid="keyword-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTerm())}
              placeholder="z. B. STIG ROCK"
              className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
            />
            <Button
              data-testid="add-keyword-button"
              onClick={addTerm}
              className="rounded-md bg-[#0A0A0A] px-4 text-white hover:bg-[#262626]"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2" data-testid="keyword-chips">
            {terms.length === 0 && <span className="text-sm text-[#8B8A8E]">Noch keine Begriffe.</span>}
            {terms.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 rounded-sm border border-[#E8E4DF] bg-[#F4F1EC] px-3 py-1.5 text-sm font-medium text-[#0A0A0A]"
              >
                {t}
                <button
                  data-testid={`remove-keyword-${t}`}
                  onClick={() => setTerms(terms.filter((x) => x !== t))}
                  className="rounded-full p-0.5 text-[#A6A4A9] transition-colors hover:bg-[#E8E4DF] hover:text-[#0A0A0A]"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>

          <div className="mt-7">
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Plattformen</h4>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {PLATFORMS.map((p) => {
                const meta = PLATFORM_META[p];
                const active = platforms.includes(p);
                return (
                  <button
                    key={p}
                    data-testid={`platform-${p}`}
                    onClick={() => togglePlatform(p)}
                    className={`flex flex-col items-center gap-2 rounded-md border px-3 py-4 text-sm font-semibold transition-all duration-300 ${
                      active
                        ? "border-[#0A0A0A] bg-[#FAF7F2] text-[#0A0A0A] ring-1 ring-[#0A0A0A]"
                        : "border-[#E8E4DF] text-[#A6A4A9] hover:bg-[#FAF7F2]"
                    }`}
                  >
                    <span className={`flex h-10 w-10 items-center justify-center rounded-md ${active ? "bg-[#0A0A0A]" : "bg-[#E8E4DF]"}`}>
                      <meta.icon className={`h-5 w-5 ${active ? "text-white" : "text-[#A6A4A9]"}`} strokeWidth={1.6} />
                    </span>
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            data-testid="start-scan-button"
            onClick={startScan}
            disabled={scanning}
            className="mt-7 w-full rounded-md bg-[#0A0A0A] py-6 text-base font-semibold tracking-wide text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#262626] active:scale-[0.98] disabled:opacity-70"
          >
            <Radar className="mr-2 h-5 w-5" />
            {scanning ? "Scan läuft …" : "Scan starten"}
          </Button>

          {scanning && (
            <div className="mt-5">
              <Progress value={progress} className="h-2.5 bg-[#E8E4DF] [&>div]:bg-[#0A0A0A]" />
              <p className="mt-2 text-xs text-[#8B8A8E]">
                Durchsuche Plattformen & bewerte Profile mit KI …
              </p>
            </div>
          )}
        </Card>

        <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8 lg:col-span-2">
          <div className="flex items-center gap-2 text-[#0A0A0A]">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A]">
              <Sparkles className="h-4 w-4 text-white" />
            </span>
            <h3 className="font-heading text-lg font-bold">Ergebnis</h3>
          </div>

          {!result && !scanning && (
            <div className="mt-10 flex flex-col items-center justify-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-md border border-[#E8E4DF] bg-[#F4F1EC]">
                <Search className="h-7 w-7 text-[#A6A4A9]" />
              </div>
              <p className="mt-4 text-sm text-[#8B8A8E]">
                Starte einen Scan, um verdächtige Profile zu finden.
              </p>
            </div>
          )}

          {result && (
            <div className="mt-5 space-y-4 animate-fade-up" data-testid="scan-result">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md bg-[#F4F1EC] p-4">
                  <p className="font-heading text-2xl font-bold tabular-nums text-[#0A0A0A]">{result.scanned}</p>
                  <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-[#8B8A8E]">Geprüft</p>
                </div>
                <div className="rounded-md bg-amber-50 p-4">
                  <p className="font-heading text-2xl font-bold tabular-nums text-amber-700">{result.violations}</p>
                  <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-amber-700">Verstöße</p>
                </div>
                <div className="rounded-md bg-emerald-50 p-4">
                  <p className="font-heading text-2xl font-bold tabular-nums text-emerald-700">{result.created}</p>
                  <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-emerald-700">Neu</p>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md border border-[#E8E4DF] bg-[#F4F1EC] p-4 text-xs text-[#0A0A0A]">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0A0A0A]" />
                <span>
                  {result.created} Fälle wurden automatisch in die Fallakte übernommen (Status &bdquo;Gefunden&ldquo;).
                </span>
              </div>

              <Button
                data-testid="goto-cases-button"
                onClick={() => navigate("/fallakte")}
                className="w-full rounded-md border border-[#E0DCD6] bg-white text-[#0A0A0A] shadow-sm hover:bg-[#FAF7F2]"
              >
                Zur Fallakte
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
